import React, { useState, useEffect, useCallback, memo, useRef } from 'react';
import { FixedSizeList, VariableSizeList } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import ResizeHandle from './ResizeHandle';
import { components } from '../utils/designTokens';
import { TableHeaderCell, TableHeaderRow } from './TablePrimitives';

/**
 * ResizableTable - A reusable table component with user-resizable columns
 *
 * Features:
 * - User-resizable columns via drag handles (custom implementation)
 * - Auto-fit to viewport (no horizontal scroll)
 * - Persistent column widths via localStorage
 * - Works with react-window virtualization
 * - Supports percentage-based or fixed pixel widths
 *
 * IMPORTANT - Parent Container Requirements:
 * This component uses AutoSizer which requires its ancestors to have explicit height.
 * The parent container MUST propagate height properly. Use one of these patterns:
 *
 *   // Pattern 1: Direct child of flex container with defined height
 *   <div className="h-full flex flex-col">
 *     <div className="flex-1 min-h-0">
 *       <ResizableTable ... />
 *     </div>
 *   </div>
 *
 *   // Pattern 2: Direct child of layout component (ListPageLayout, TabbedPageLayout)
 *   <ListPageLayout ...>
 *     <ResizableTable ... />
 *   </ListPageLayout>
 *
 * DO NOT wrap in divs without height (e.g., <div className="space-y-6">) as this
 * breaks the height chain and AutoSizer will receive 0 height, rendering no rows.
 *
 * @param {Array} columns - Array of { key, label, defaultWidth OR defaultWidthPercent, minWidth }
 *   - defaultWidth: Fixed pixel width (e.g., 200)
 *   - defaultWidthPercent: Percentage of container (e.g., 15 for 15%)
 * @param {Array} data - Array of row data
 * @param {Function} renderRow - Function to render each virtualized row
 * @param {String} storageKey - localStorage key for persisting widths
 * @param {Number} rowHeight - Height of each row (default: 38). Used when getRowHeight is not provided.
 * @param {Function} getRowHeight - Optional. If provided, switches to VariableSizeList. Called with
 *   (index, data) → number. Other tables without this prop use FixedSizeList unchanged.
 * @param {String} containerHeight - CSS height for table container
 */
const ResizableTable = ({
  columns,
  data,
  renderRow,
  storageKey,
  onOpen,
  selectedSessionId,
  selectedTraceId,
  rowHeight = 38,
  getRowHeight = null,
  containerHeight = 'calc(100vh - 350px)'
}) => {
  // Calculate column widths from percentages or fixed widths
  const calculateColumnWidths = useCallback((containerWidth) => {
    // Account for spacers between columns (3px each)
    const numSpacers = columns.length - 1;
    const spacerWidth = 3;
    const totalSpacerWidth = numSpacers * spacerWidth;
    const availableWidth = containerWidth - totalSpacerWidth;

    console.log('[ResizableTable] Width calculation:', {
      containerWidth,
      numColumns: columns.length,
      numSpacers,
      totalSpacerWidth,
      availableWidth,
      storageKey
    });

    const widths = columns.reduce((acc, col) => {
      if (col.defaultWidthPercent) {
        // Percentage-based width (calculated from available width after spacers)
        const calculatedWidth = (availableWidth * col.defaultWidthPercent) / 100;
        const finalWidth = Math.max(col.minWidth || 70, calculatedWidth);
        acc[col.key] = finalWidth;

        if (finalWidth > calculatedWidth) {
          console.log(`[ResizableTable] Column ${col.key} hit minWidth:`, {
            calculated: calculatedWidth,
            minWidth: col.minWidth,
            using: finalWidth
          });
        }
      } else {
        // Fixed pixel width
        acc[col.key] = col.defaultWidth || 100;
      }
      return acc;
    }, {});

    const totalColumnWidth = Object.values(widths).reduce((sum, w) => sum + w, 0);
    const totalWidthWithSpacers = totalColumnWidth + totalSpacerWidth;

    console.log('[ResizableTable] Calculated widths:', {
      widths,
      totalColumnWidth,
      totalWidthWithSpacers,
      overflow: totalWidthWithSpacers - containerWidth
    });

    return widths;
  }, [columns, storageKey]);

  // Initialize column widths from localStorage or defaults (with validation)
  const [columnWidths, setColumnWidths] = useState(() => {
    const saved = localStorage.getItem(storageKey);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        // Validate that all current columns have widths
        const allColumnsPresent = columns.every(col => parsed[col.key] !== undefined);
        if (allColumnsPresent) {
          // All columns have saved widths - enforce minWidth constraints
          const constrainedWidths = {};
          columns.forEach(col => {
            const savedWidth = parsed[col.key];
            const minWidth = col.minWidth || 70;
            constrainedWidths[col.key] = Math.max(minWidth, savedWidth);
          });
          return constrainedWidths;
        } else {
          // Some columns are missing (schema changed) - clear and recalculate
          console.log('[ResizableTable] Column schema changed, clearing saved widths');
          localStorage.removeItem(storageKey);
        }
      } catch (e) {
        console.error('Failed to parse saved column widths:', e);
      }
    }

    // No saved widths - initialize with default fixed widths immediately
    // (percentage-based widths will be recalculated in useEffect with actual container width)
    const hasPercentageWidths = columns.some(col => col.defaultWidthPercent);
    if (!hasPercentageWidths) {
      // All columns use fixed widths - initialize immediately
      return columns.reduce((acc, col) => {
        acc[col.key] = col.defaultWidth || 100;
        return acc;
      }, {});
    }

    // Has percentage-based widths - will be calculated in useEffect with actual container width
    return {};
  });

  // Track if resizing (to prevent auto-fit during resize)
  const [isResizing, setIsResizing] = useState(false);

  // Ref for resize timeout cleanup
  const resizeTimeoutRef = useRef(null);

  // Ref for container element
  const containerRef = useRef(null);

  // Refs for syncing horizontal scroll between header and body
  const headerScrollRef = useRef(null);
  const bodyScrollRef = useRef(null);

  // Sync scroll between header and body
  const syncScroll = useCallback((source) => {
    if (source === 'header' && headerScrollRef.current && bodyScrollRef.current) {
      bodyScrollRef.current.scrollLeft = headerScrollRef.current.scrollLeft;
    } else if (source === 'body' && bodyScrollRef.current && headerScrollRef.current) {
      headerScrollRef.current.scrollLeft = bodyScrollRef.current.scrollLeft;
    }
  }, []);

  // Calculate fitted widths to avoid horizontal scrolling
  const calculateFittedWidths = useCallback((cols, viewportWidth) => {
    const totalDefaultWidth = cols.reduce((sum, col) => sum + (col.defaultWidth || 100), 0);

    if (totalDefaultWidth > viewportWidth) {
      // Scale down proportionally while respecting minimum widths
      const scale = viewportWidth / totalDefaultWidth;
      return cols.reduce((acc, col) => {
        const scaledWidth = col.defaultWidth * scale;
        acc[col.key] = Math.max(col.minWidth || 70, scaledWidth);
        return acc;
      }, {});
    }

    return cols.reduce((acc, col) => {
      acc[col.key] = col.defaultWidth;
      return acc;
    }, {});
  }, []);

  // Initialize widths on mount and handle window resize
  useEffect(() => {
    const handleResize = () => {
      if (isResizing) return; // Don't auto-fit while user is resizing
      if (!containerRef.current) return;

      const containerWidth = containerRef.current.offsetWidth - 40; // Account for scrollbar and padding

      // Only recalculate if there are no saved widths (first time initialization)
      const shouldRecalculate = !localStorage.getItem(storageKey) && Object.keys(columnWidths).length === 0;

      if (shouldRecalculate) {
        const newWidths = calculateColumnWidths(containerWidth);
        setColumnWidths(newWidths);
      }
    };

    // Initial calculation with small delay to ensure container is sized
    const timer = setTimeout(handleResize, 100);

    window.addEventListener('resize', handleResize);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', handleResize);
    };
  }, [columns, storageKey, calculateColumnWidths, isResizing, columnWidths]);

  // Persist column widths to localStorage (only if we have widths)
  useEffect(() => {
    if (Object.keys(columnWidths).length > 0) {
      localStorage.setItem(storageKey, JSON.stringify(columnWidths));
    }
  }, [columnWidths, storageKey]);

  // Handle column resize with minimum width enforcement
  const handleColumnResize = useCallback((columnKey, delta) => {
    setIsResizing(true);

    // Clear existing timeout
    if (resizeTimeoutRef.current) {
      clearTimeout(resizeTimeoutRef.current);
    }

    setColumnWidths(prev => {
      const column = columns.find(c => c.key === columnKey);
      const minWidth = column?.minWidth || 70;
      const newWidth = Math.max(minWidth, prev[columnKey] + delta);

      return {
        ...prev,
        [columnKey]: newWidth
      };
    });

    // Reset after 200ms of no resize activity
    resizeTimeoutRef.current = setTimeout(() => {
      setIsResizing(false);
    }, 200);
  }, [columns]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
    };
  }, []);

  // Extract handle width from design token
  const handleWidth = parseInt(components.table.resizeHandle.width);

  return (
    <div className={components.table.container} ref={containerRef} data-resizable-table-container>
      {/* Table Header with Resize Handles - overflow-x-auto enables horizontal scrolling, scrollbar hidden */}
      <TableHeaderRow
        ref={headerScrollRef}
        className={`flex items-center px-4 ${components.table.header.height} flex-shrink-0 ${components.table.header.sticky} overflow-x-auto overflow-y-hidden [&::-webkit-scrollbar]:hidden`}
        onScroll={() => syncScroll('header')}
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', minWidth: 'max-content', height: '100%' }}>
          {columns.map((col, i) => (
            <React.Fragment key={col.key}>
              <TableHeaderCell
                compact
                style={{
                  width: `${columnWidths[col.key]}px`,
                  minWidth: `${col.minWidth || 70}px`,
                  flex: 'none'
                }}
              >
                {col.label}
              </TableHeaderCell>
              {/* Add resize handle between columns (not after last column) */}
              {i < columns.length - 1 && (
                <ResizeHandle
                  onResize={(delta) => handleColumnResize(col.key, delta)}
                />
              )}
            </React.Fragment>
          ))}
          {/* Spacer to match resize handles space in data rows */}
          <div style={{ width: `${(columns.length - 1) * handleWidth}px`, flex: 'none' }} />
        </div>
      </TableHeaderRow>

      {/* Virtualized Table Body - overflow-x-auto enables horizontal scrolling */}
      <div
        ref={bodyScrollRef}
        className="flex-1 overflow-x-auto overflow-y-hidden"
        style={{ height: containerHeight }}
        onScroll={() => syncScroll('body')}
      >
        <AutoSizer>
          {({ height, width }) => {
            // Wait for columnWidths to be initialized before rendering
            const hasWidths = Object.keys(columnWidths).length > 0;
            if (!hasWidths || height === 0) {
              return null;
            }

            // Calculate total content width (columns + spacers)
            const totalColumnWidth = Object.values(columnWidths).reduce((sum, w) => sum + w, 0);
            const totalSpacerWidth = (columns.length - 1) * 3; // 3px spacers
            const totalContentWidth = totalColumnWidth + totalSpacerWidth + ((columns.length - 1) * handleWidth);
            const listWidth = Math.max(width, totalContentWidth);

            const itemData = { data, columnWidths, onOpen, selectedSessionId, selectedTraceId };

            if (getRowHeight) {
              return (
                <VariableSizeList
                  height={height}
                  itemCount={data.length}
                  itemSize={(index) => getRowHeight(index, data)}
                  width={listWidth}
                  itemData={itemData}
                  style={{ overflowX: 'visible' }}
                >
                  {renderRow}
                </VariableSizeList>
              );
            }

            return (
              <FixedSizeList
                height={height}
                itemCount={data.length}
                itemSize={rowHeight}
                width={listWidth}
                itemData={itemData}
                style={{ overflowX: 'visible' }}
              >
                {renderRow}
              </FixedSizeList>
            );
          }}
        </AutoSizer>
      </div>
    </div>
  );
};

export default memo(ResizableTable);
